import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudies, exchangeSSOToken, storeTokensForPolling } from '@/taskpane/services/sso';
import type { Office } from '@/taskpane/services/sso';
import { AuthContext } from '@/taskpane/contexts/auth/AuthContext';
import LoadingButton from '@/taskpane/components/button/LoadingButton';
import LoadingScreen from '@/taskpane/components/loading/LoadingScreen';
import { enqueueSnackbar } from 'notistack';
import { 
  Box, 
  Card, 
  Stack, 
  Typography, 
  RadioGroup, 
  FormControlLabel, 
  Radio,
  Alert,
} from '@mui/material';

/**
 * Office Selection Page
 * 
 * Displayed when a user authenticates via SSO and belongs to multiple notary offices.
 * User must select which office they want to use for this session.
 * 
 * The selected office's TenantId (CRPCEN) will be mapped to custom:CRPCEN in Cognito.
 */
export function OfficeSelectionPage() {
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setTokens } = useContext(AuthContext);
  const navigate = useNavigate();
  
  useEffect(() => {
    loadOffices();
  }, []);
  
  const loadOffices = async () => {
    try {
      console.log('[Office Selection] Loading user offices');
      
      // Get temporary GenAPI token from session storage
      const genapiToken = sessionStorage.getItem('genapi_token_temp');
      
      if (!genapiToken) {
        console.error('[Office Selection] No GenAPI token found in session');
        enqueueSnackbar('Session expirée - veuillez vous reconnecter', { variant: 'error' });
        navigate('/login');
        return;
      }
      
      // Fetch user's offices from GenAPI
      const studiesList = await getStudies(genapiToken);
      
      if (!studiesList || studiesList.length === 0) {
        enqueueSnackbar('Aucune étude trouvée pour cet utilisateur', { variant: 'error' });
        navigate('/login');
        return;
      }
      
      setOffices(studiesList);
      
      // Store the list of available offices for future reference
      localStorage.setItem('available_offices', JSON.stringify(studiesList));
      
      // Auto-select if only one office (shouldn't happen, but just in case)
      if (studiesList.length === 1) {
        setSelectedOffice(studiesList[0].TenantId);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('[Office Selection] Error loading offices:', error);
      enqueueSnackbar('Erreur lors du chargement des études', { variant: 'error' });
      navigate('/login');
    }
  };
  
  const handleOfficeSelection = async () => {
    if (!selectedOffice) {
      enqueueSnackbar('Veuillez sélectionner une étude', { variant: 'warning' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('[Office Selection] Selected office:', selectedOffice);
      
      // Get temporary GenAPI token
      const genapiToken = sessionStorage.getItem('genapi_token_temp');
      
      if (!genapiToken) {
        enqueueSnackbar('Session expirée - veuillez vous reconnecter', { variant: 'error' });
        navigate('/login');
        return;
      }
      
      // Store selected office for future reference
      localStorage.setItem('selected_office_tenant_id', selectedOffice);
      
      // Find selected office details
      const selectedOfficeData = offices.find(o => o.TenantId === selectedOffice);
      if (selectedOfficeData) {
        localStorage.setItem('selected_office_name', selectedOfficeData.Nom);
        // Store CRPCEN if available (some studies may not have it yet)
        localStorage.setItem('selected_office_crpcen', selectedOfficeData.NumeroCRPCEN || '');
      }
      
      // Exchange GenAPI token for Cognito tokens
      // The backend will use the tenantid from the GenAPI JWT to set custom:CRPCEN
      console.log('[Office Selection] Exchanging GenAPI token for Cognito tokens');
      const cognitoTokens = await exchangeSSOToken(genapiToken);
      
      // Store tokens in backend for cross-context retrieval (if state is available)
      const storedState = sessionStorage.getItem('sso_state_temp');
      if (storedState) {
        try {
          console.log('[Office Selection] Storing tokens for cross-context SSO with state:', storedState.substring(0, 10) + '...');
          await storeTokensForPolling(storedState, cognitoTokens);
          console.log('[Office Selection] ✓ Tokens stored successfully for polling');
        } catch (error) {
          console.error('[Office Selection] Failed to store tokens for polling (non-fatal):', error);
          // Non-fatal error - continue with local storage
        }
      }
      
      // Clear temporary tokens and state
      sessionStorage.removeItem('genapi_token_temp');
      sessionStorage.removeItem('sso_state_temp');
      
      // Store Cognito tokens in context
      setTokens(cognitoTokens);
      
      console.log('[Office Selection] Successfully authenticated');
      enqueueSnackbar('Étude sélectionnée avec succès', { variant: 'success' });
      
      // Redirect to main app
      navigate('/folders');
      
    } catch (error) {
      console.error('[Office Selection] Error during office selection:', error);
      enqueueSnackbar('Erreur lors de la sélection de l\'étude', { variant: 'error' });
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <LoadingScreen 
        message="Chargement de vos études..."
        submessage="Veuillez patienter"
      />
    );
  }
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 3,
        }}
      >
        <Stack spacing={3}>
          <Typography variant="h5" textAlign="center" fontWeight="bold">
            Sélectionnez votre étude
          </Typography>
          
          <Alert severity="info">
            Vous compte est relié à plusieurs études. Veuillez sélectionner celle que vous souhaitez utiliser.
          </Alert>
          
          <RadioGroup
            value={selectedOffice}
            onChange={(e) => setSelectedOffice(e.target.value)}
          >
            {offices.map((office) => (
              <FormControlLabel
                key={office.TenantId}
                value={office.TenantId}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {office.Nom}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      CRPCEN: {office.NumeroCRPCEN}
                    </Typography>
                  </Box>
                }
                sx={{
                  border: '1px solid',
                  borderColor: selectedOffice === office.TenantId ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  p: 2,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              />
            ))}
          </RadioGroup>
          
          <LoadingButton
            onClick={handleOfficeSelection}
            disabled={!selectedOffice}
            loading={isSubmitting}
            variant="contained"
            fullWidth
            size="large"
          >
            Valider la sélection
          </LoadingButton>
          
          <Typography variant="caption" color="textSecondary" textAlign="center">
            Vous pourrez changer d'étude ultérieurement dans les paramètres de votre compte.
          </Typography>
        </Stack>
      </Card>
    </Box>
  );
}
